/**
 * Balance-sheet document model.
 *
 * Two layers live here:
 *
 * 1. The **translated-balance-sheet types** (IFRS SME Section 30 — foreign
 *    currency translation), moved verbatim out of
 *    `apps/web/src/types/translated-financials.ts` so the server-side assembler
 *    and the on-screen report share one contract. That file now re-exports
 *    them, so every existing web importer is untouched.
 * 2. The assembled **BalanceSheetDocument** — everything a template prints,
 *    fully resolved: the template fetches nothing and derives nothing. That
 *    includes the balance check, which used to be computed inline in
 *    `BalanceSheetPreview` (`balanceSheetBalanceCheck` below is now called by
 *    both the preview and this model).
 *
 * Each interface is mirrored by a zod schema carrying a
 * `satisfies z.ZodType<...>` guard: the hand-written interface stays the source
 * of truth (it keeps the doc comments the moved code depends on) and the guard
 * turns any drift between the two into a compile error — the same technique
 * `documentLanguageSchema` uses in `../common.ts`.
 */
import { z } from "zod";
import { documentMetaSchema, orgIdentityBlockSchema, } from "../common.js";
import { BALANCE_SHEET_RESULT_KINDS, } from "../../lib/balanceSheet.js";
/* -------------------------------------------------------------------------- */
/* Native (per-currency) layer — zod mirrors of `../../lib/balanceSheet.ts`    */
/* -------------------------------------------------------------------------- */
const multiCurrencyTotalSchema = z.record(z.string(), z.number());
const resultKindSchema = z.enum(BALANCE_SHEET_RESULT_KINDS);
const balanceSheetItemSchema = z.object({
    name: z.string(),
    amount: z.number(),
    currency: z.string(),
    id: z.string().optional(),
    kind: resultKindSchema.optional(),
});
/** Zod mirror of `BalanceSheetData` (the `sourceData` a document carries). */
export const nativeBalanceSheetDataSchema = z.object({
    assets: z.object({
        current: z.array(balanceSheetItemSchema),
        nonCurrent: z.array(balanceSheetItemSchema),
    }),
    liabilities: z.object({
        current: z.array(balanceSheetItemSchema),
        nonCurrent: z.array(balanceSheetItemSchema),
    }),
    equity: z.array(balanceSheetItemSchema),
    totals: z.object({
        currentAssets: multiCurrencyTotalSchema,
        nonCurrentAssets: multiCurrencyTotalSchema,
        totalAssets: multiCurrencyTotalSchema,
        currentLiabilities: multiCurrencyTotalSchema,
        nonCurrentLiabilities: multiCurrencyTotalSchema,
        totalLiabilities: multiCurrencyTotalSchema,
        totalEquity: multiCurrencyTotalSchema,
    }),
    accountCurrencies: z.array(z.string()),
});
/** Zod mirror of `TranslationExchangeRate` (`../currencyTranslation.ts`). */
export const translationExchangeRateSchema = z.object({
    fromCurrency: z.string(),
    toCurrency: z.string(),
    rate: z.number(),
    rateDate: z.string(),
});
export const translatedBalanceSheetItemSchema = z.object({
    name: z.string(),
    id: z.string().optional(),
    kind: resultKindSchema.optional(),
    nativeAmount: z.number(),
    nativeCurrency: z.string(),
    translatedAmount: z.number(),
    presentationCurrency: z.string(),
    exchangeRate: z.number(),
    rateDate: z.string(),
});
export const translationAdjustmentSchema = z.object({
    amount: z.number(),
    currency: z.string(),
    description: z.string(),
});
export const translationDisclosuresSchema = z.object({
    exchangeRatesUsed: z.array(translationExchangeRateSchema),
    translationMethod: z.literal("closing_rate"),
    functionalCurrencies: z.array(z.string()),
    presentationCurrency: z.string(),
    closingDate: z.string(),
});
/** The presentation-currency totals block of a translated balance sheet. */
export const translatedBalanceSheetTotalsSchema = z.object({
    currentAssets: z.number(),
    nonCurrentAssets: z.number(),
    totalAssets: z.number(),
    currentLiabilities: z.number(),
    nonCurrentLiabilities: z.number(),
    totalLiabilities: z.number(),
    totalEquityBeforeCTA: z.number(),
    totalEquity: z.number(),
    totalLiabilitiesAndEquity: z.number(),
});
export const translatedBalanceSheetDataSchema = z.object({
    presentationCurrency: z.string(),
    closingDate: z.string(),
    assets: z.object({
        current: z.array(translatedBalanceSheetItemSchema),
        nonCurrent: z.array(translatedBalanceSheetItemSchema),
    }),
    liabilities: z.object({
        current: z.array(translatedBalanceSheetItemSchema),
        nonCurrent: z.array(translatedBalanceSheetItemSchema),
    }),
    equity: z.array(translatedBalanceSheetItemSchema),
    totals: translatedBalanceSheetTotalsSchema,
    hasForeignCurrency: z.boolean(),
    cumulativeTranslationAdjustment: translationAdjustmentSchema,
    unexplainedDifference: z.number(),
    disclosures: translationDisclosuresSchema,
    sourceData: nativeBalanceSheetDataSchema,
});
/* -------------------------------------------------------------------------- */
/* Balance check                                                              */
/* -------------------------------------------------------------------------- */
/**
 * Tolerance under which an `Assets − (Liabilities + Equity)` difference counts
 * as float noise rather than an out-of-balance condition. Kept exactly as it
 * was inline in `BalanceSheetPreview` (`Math.abs(difference) < 0.01`).
 */
export const BALANCE_SHEET_TOLERANCE = 0.01;
/**
 * The report's balance check — extracted from `BalanceSheetPreview` so the
 * screen and the PDF reach the same verdict from one implementation.
 *
 * In a single-currency org the assembler does NOT plug the A − L − E residual
 * into equity, so a genuine out-of-balance condition makes these totals
 * honestly diverge and this check fires.
 *
 * Takes only the `totals` block so a caller with no data yet (the preview's
 * zeroed fallback) can still ask.
 */
export function balanceSheetBalanceCheck(translated) {
    const difference = translated.totals.totalAssets - translated.totals.totalLiabilitiesAndEquity;
    return {
        difference,
        isBalanced: Math.abs(difference) < BALANCE_SHEET_TOLERANCE,
    };
}
export const balanceSheetBalanceCheckSchema = z.object({
    isBalanced: z.boolean(),
    difference: z.number(),
});
export const balanceSheetRatesStatusSchema = z.object({
    allRatesFound: z.boolean(),
    missingCurrencies: z.array(z.string()),
});
export const balanceSheetComparativeBlockSchema = z.object({
    asOfDate: z.string(),
    translated: translatedBalanceSheetDataSchema,
    ratesStatus: balanceSheetRatesStatusSchema,
    balanceCheck: balanceSheetBalanceCheckSchema,
});
export const balanceSheetComparativeStatusSchema = z.enum([
    "included",
    "unavailable",
]);
export const balanceSheetDocumentSchema = z.object({
    report: z.literal("balance-sheet"),
    meta: documentMetaSchema,
    org: orgIdentityBlockSchema,
    asOfDate: z.string(),
    presentationCurrency: z.string(),
    translated: translatedBalanceSheetDataSchema,
    ratesStatus: balanceSheetRatesStatusSchema,
    balanceCheck: balanceSheetBalanceCheckSchema,
    priorAsOfDate: z.string(),
    comparativeStatus: balanceSheetComparativeStatusSchema,
    comparative: balanceSheetComparativeBlockSchema.nullable(),
});
//# sourceMappingURL=model.js.map